import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { BiDotsVerticalRounded } from 'react-icons/bi'

export type SsRowActionItem = {
  key: string
  label: string
  icon: ReactNode
  onClick: () => void
  hidden?: boolean
  tooltipId?: string
  tooltipContent?: string
}

type Props = {
  actions: SsRowActionItem[]
  menuId: string
  appendInline?: ReactNode
  appendMenu?: ReactNode
}

type MenuCoords = {
  top: number
  left: number
}

const ESTIMATED_ITEM_HEIGHT = 40
const ESTIMATED_PADDING = 12
const ESTIMATED_PANEL_WIDTH = 220

const SsRowActions = ({ actions, menuId, appendInline, appendMenu }: Props) => {
  const visibleActions = actions.filter((action) => !action.hidden)
  const [menuOpen, setMenuOpen] = useState(false)
  const [coords, setCoords] = useState<MenuCoords | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  const computeCoords = () => {
    const trigger = triggerRef.current
    if (!trigger) return

    const rect = trigger.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight

    const panel = panelRef.current
    const panelWidth = panel?.offsetWidth || ESTIMATED_PANEL_WIDTH
    const estimatedHeight =
      visibleActions.length * ESTIMATED_ITEM_HEIGHT + ESTIMATED_PADDING + (appendMenu ? ESTIMATED_ITEM_HEIGHT : 0)
    const panelHeight = panel?.offsetHeight || estimatedHeight

    // Position horizontally: prefer opening to the left of the 3-dot button
    let left = rect.left - panelWidth - 4
    if (left < 8) {
      if (rect.right + panelWidth + 8 <= vw) {
        left = rect.right + 4
      } else {
        left = Math.max(8, vw - panelWidth - 8)
      }
    }

    // Position vertically: center relative to the 3-dot button, clamped inside viewport
    let top = rect.top + rect.height / 2 - panelHeight / 2
    top = Math.max(8, Math.min(top, vh - panelHeight - 8))

    setCoords({ top, left })
  }

  useLayoutEffect(() => {
    if (!menuOpen) {
      setCoords(null)
      return
    }

    computeCoords()
    const rafId = requestAnimationFrame(() => {
      computeCoords()
    })
    return () => cancelAnimationFrame(rafId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuOpen])

  useEffect(() => {
    if (!menuOpen) return undefined

    const tr = triggerRef.current?.closest('tr')
    const td = triggerRef.current?.closest('td')
    if (tr) tr.classList.add('ss-row--actions-open')
    if (td) td.classList.add('ss-cell--actions-open')

    const onDocumentClick = (event: MouseEvent) => {
      const target = event.target as Node
      if (
        triggerRef.current?.contains(target) ||
        panelRef.current?.contains(target)
      ) {
        return
      }
      setMenuOpen(false)
    }

    const onReposition = () => {
      const trigger = triggerRef.current
      if (!trigger) return
      const rect = trigger.getBoundingClientRect()
      if (rect.bottom < 0 || rect.top > window.innerHeight) {
        setMenuOpen(false)
        return
      }
      computeCoords()
    }

    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', onDocumentClick)
    document.addEventListener('keydown', onEscape)
    window.addEventListener('resize', onReposition)
    window.addEventListener('scroll', onReposition, true)

    return () => {
      if (tr) tr.classList.remove('ss-row--actions-open')
      if (td) td.classList.remove('ss-cell--actions-open')
      document.removeEventListener('mousedown', onDocumentClick)
      document.removeEventListener('keydown', onEscape)
      window.removeEventListener('resize', onReposition)
      window.removeEventListener('scroll', onReposition, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuOpen])

  if (visibleActions.length === 0) {
    return null
  }

  return (
    <div className='d-flex align-items-center justify-content-end ss-row-actions-wrap'>
      {/* Desktop icons ≥1200px — controlled by CSS (.ss-actions-desktop) */}
      <div className='ss-row-actions-inline ss-actions-desktop'>
        {visibleActions.map((action) => (
          <div
            key={action.key}
            data-tooltip-id={action.tooltipId ?? 'ss-row-action-tooltip'}
            data-tooltip-content={action.tooltipContent ?? action.label}
            data-tooltip-place='left'
            className='btn btn-icon receipt-action-btn btn-active-color-primary btn-sm ss-row-action-btn'
            onClick={(event) => {
              event.stopPropagation()
              action.onClick()
            }}
          >
            {action.icon}
          </div>
        ))}
        {appendInline}
      </div>

      {/* Compact 3-dot menu <1200px — controlled by CSS (.ss-actions-compact) */}
      <div
        className={`ss-row-actions-menu ss-actions-compact${menuOpen ? ' ss-row-actions-menu--open' : ''}`}
      >
        <button
          ref={triggerRef}
          type='button'
          id={menuId}
          className={`btn btn-transparent remove-arrow ss-row-actions-menu__toggle${menuOpen ? ' active' : ''}`}
          aria-label='Actions'
          aria-expanded={menuOpen}
          aria-haspopup='menu'
          onClick={(event) => {
            event.stopPropagation()
            setMenuOpen((open) => !open)
          }}
        >
          <BiDotsVerticalRounded size={20} />
        </button>

        {menuOpen &&
          typeof document !== 'undefined' &&
          createPortal(
            <div
              ref={panelRef}
              className='ss-row-actions-menu__panel'
              role='menu'
              aria-labelledby={menuId}
              style={{
                position: 'fixed',
                top: coords ? `${coords.top}px` : '-9999px',
                left: coords ? `${coords.left}px` : '-9999px',
                right: 'auto',
                transform: 'none',
                zIndex: 999999,
                visibility: coords ? 'visible' : 'hidden',
              }}
            >
              {visibleActions.map((action) => (
                <button
                  key={action.key}
                  type='button'
                  role='menuitem'
                  className='ss-row-actions-menu__item'
                  onClick={(event) => {
                    event.stopPropagation()
                    setMenuOpen(false)
                    action.onClick()
                  }}
                >
                  <span className='ss-row-actions-menu__icon'>{action.icon}</span>
                  <span className='ss-row-actions-menu__text'>{action.label}</span>
                </button>
              ))}
              {appendMenu}
            </div>,
            document.body
          )}
      </div>
    </div>
  )
}

export default SsRowActions
